<template>
  <div class="task-list">
    <div class="tasks" :class="[density]">
      <task ref="roottask"
            :key="task.id"
            @noteUserAction="noteUserAction"
            :currentListId="currentListId"
            :initialJsTask="task"
            :indentLevel=-1
            :idMap="idMap"
            :updater="updater"
            :density="density"></task>
      <div v-if="orphanedTask != null" class="orphaned-header">Orphaned Tasks</div>
      <task ref="orphanedtasks"
            v-if="orphanedTask != null"
            :key="orphanedTask.id"
            @noteUserAction="noteUserAction"
            :currentListId="currentListId"
            :initialJsTask="orphanedTask"
            :indentLevel=-1
            :idMap="idMap"
            :updater="updater"
            :density="density"></task>
    </div>
  </div>
</template>

<script>
import Task from './Task.vue'

/**
 * This element contains all the data for a list. This object holds a root
 * "task" which is not actually displayed, because many of the functions that
 * are necessary for manipulating the list (adding new tasks, removing tasks)
 * are best performed on the parent task. This root task contains all the tasks
 * in the list.
 *
 * The tasks are JsTask objects. The IDs for these are locally generated and
 * mapped to IDs from the gTasks. This is done in order to make it possible to
 * create a local copy of the task and update it before sending the API call to
 * the server.
 */
export default {
  name: 'task-list',

  components: {
    Task
  },

  props: [
    'currentListId',
    'updater',
    'density'
  ],

  data () {
    return {
      // A JsTask object.
      task: null,

      // A JsTask object representing the root of any orphaned tasks.
      orphanedTask: null,

      idMap: new this.$IdMap(),

      // Bool indicating if the user is logged in.
      loggedIn: false,

      // Whether to show hidden tasks.
      showHidden: false,

      // Holds the last time the user performed an action.
      lastUserActionTime: Date.now()
    }
  },

  methods: {
    updateTasks: async function () {
      this.$emit('update:loading', true)
      try {
        if (this.loggedIn && this.currentListId) {
          let nextPageToken = null
          let allItems = []
          // TODO Handle hidden tasks. If a task has a hidden parent, what
          // should be done?
          let requestCount = 0
          do {
            ++requestCount
            let resolveFn = null
            let rejectFn = null
            let promise = new Promise((resolve, reject) => {
              resolveFn = resolve
              rejectFn = reject
            })
            this.updater.newListTasks(
              this.currentListId,
              this.showHidden,
              nextPageToken,
              response => {
                if (response.status === 200) {
                  if (response.result.items != null) {
                    allItems.push(...response.result.items)
                  }
                  nextPageToken = response.result.nextPageToken
                  resolveFn()
                } else {
                  console.error('Failed to get tasks: ' + JSON.stringify(response.result.error))
                  rejectFn()
                }
              }
            )
            this.updater.flush()
            await promise
          } while (nextPageToken != null && requestCount < 5)
          let convertedTasks = this.convertTasksResponse(allItems)
          this.task.subtasks = []
          this.$nextTick(() => {
            this.task.subtasks = convertedTasks
          })
          this.$emit('update:loading', false)
        } else {
          this.task.subtasks = []
          this.$emit('update:loading', false)
        }
      } catch {
        this.$emit('update:loading', false)
      }
    },

    // Converts a response from the Tasks API containing a list of the tasks to
    // a tree of JsTask objects with the children populated.
    convertTasksResponse: function (gTasks) {
      // Make sure no tasks are return with parents.
      for (let gTask of gTasks) {
        if (gTask.parent != null) {
          console.error('Expect no gTasks to have parents, but this one does')
          console.error(gTask)
          return []
        }
      }

      // Sort the tasks first, which is fine because we've verified that the
      // API does not return any tasks with parents.
      gTasks.sort((a, b) => {
        if (a.position < b.position) {
          return -1
        }
        if (a.position > b.position) {
          return 1
        }
        return 0
      })

      let rootJsTasks = []
      let parentJsTasks = []
      let currentIndentLevel = -1
      for (let gTask of gTasks) {
        // Create the task.
        let jsTaskId = this.idMap.getJsTask(gTask.id)
        let jsTask = new this.$JsTask(jsTaskId, gTask)

        // Figure out how indented this task should be, and do some bookkeeping
        // related to the indentation level.
        let numIndents = gTask.title.match(/^[~]*/)[0].length
        // Strip all the leading dots, even if they're not all used as indents,
        // just to fix any errors.
        if (numIndents > currentIndentLevel + 1) {
          // Can only increase indent level by 1 at a time.
          numIndents = currentIndentLevel + 1
        }
        currentIndentLevel = numIndents
        parentJsTasks[currentIndentLevel] = jsTask

        // Put the task under the parent.
        if (numIndents === 0) {
          rootJsTasks.push(jsTask)
        } else {
          parentJsTasks[numIndents - 1].subtasks.push(jsTask)
        }
      }
      return rootJsTasks
    },

    // Saves the tasks to a local file.
    saveTasks: function () {
      let blob = new Blob([JSON.stringify(this.task, null, 2)], { type: 'application/json' })
      let a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'tasks.json'
      a.click()
    },

    indentSelectedTask: function () {
      let taskEl = this.getSelectedTask(this.$refs.roottask)
      if (taskEl !== null) {
        taskEl.indentTask()
      }
    },

    unindentSelectedTask: function () {
      let taskEl = this.getSelectedTask(this.$refs.roottask)
      if (taskEl !== null) {
        taskEl.unindentTask()
      }
    },

    moveUpSelectedTask: function () {
      let taskEl = this.getSelectedTask(this.$refs.roottask)
      if (taskEl !== null) {
        taskEl.moveTaskUp()
      }
    },

    moveDownSelectedTask: function () {
      let taskEl = this.getSelectedTask(this.$refs.roottask)
      if (taskEl !== null) {
        taskEl.moveTaskDown()
      }
    },

    addTaskAtTop: function () {
      this.$refs.roottask.addNewTask(null)
    },

    clearCompletedTasks: function () {
      this.updater.newClear(
        this.currentListId,
        response => {
          if (response.status === 204) {
            this.showHidden = false
            this.$parent.updateTasks()
          } else {
            console.error('Failed to clear completed tasks')
          }
        }
      )
    },

    // Returns the element underneath taskEl corresponding to the selected
    // task, or null if no task is selected.
    getSelectedTask: function (taskEl) {
      if (taskEl.selected) {
        return taskEl
      }
      if (taskEl.$refs.subtask == null) {
        return null
      }
      for (let subtaskEl of taskEl.$refs.subtask) {
        let subtaskSelectedEl = this.getSelectedTask(subtaskEl)
        if (subtaskSelectedEl !== null) {
          return subtaskSelectedEl
        }
      }
      return null
    },

    noteUserAction: function () {
      this.lastUserActionTime = Date.now()
    },

    updateState: async function () {
      this.loggedIn = this.$gapi.isLoggedIn()
    }
  },

  watch: {
    loggedIn: function () {
      this.updateTasks()
    },

    currentListId: function () {
      this.updateTasks()
    },

    showHidden: function () {
      this.updateTasks()
    }
  },

  created () {
    this.task = new this.$JsTask(this.idMap.newJsTask(), this.$newGTask())
    this.task.isRoot = true
  },

  mounted () {
    this.$gapi.addAuthStateChangeCallback(this.updateTasks.bind(this))
    this.$gapi.addAuthStateChangeCallback(this.updateState.bind(this))
    this.updateTasks()
    this.updateState()

    // Update the task list if the user has been inactive for 5 minutes.
    setInterval(() => {
      if ((Date.now() - this.lastUserActionTime) > 300000) {
        console.debug('Periodic data refresh')
        this.updateTasks()
        this.lastUserActionTime = Date.now()
      }
    }, 60000)
  }
}
</script>

<style scoped>
.tasks {
  width: 100%;
  overflow: auto;
}

.tasks.desktop {
  height: calc(100% - 1.5em);
}

.tasks.mobile {
  height: calc(100% - 2.5em);
}

.orphaned-header {
  margin-top: 2em;
}
</style>
